'use client';

import { c as _c } from "react/compiler-runtime";
import { type GetMarketRow, useGetMarket, useGetMarketChart, useServerClock } from '@morfi/backend-sdk-v2/client';
import { type ReactNode, useEffect, useLayoutEffect, useState } from 'react';
import { WinnerChartIntent } from '@/components/market/winner/winner-chart-intent';
import { getAvailableIntervals, LIVE_CHART_SEED_SECONDS, resolveAvailableInterval } from '@/lib/chart/chart-factory';
import { resolveChartRequestStart } from '@/lib/chart/chart-window-boundaries';
import { resolveTerminalChartLifecycle, resolveTerminalChartOpeningEndUnix } from '@/lib/chart/terminal-chart-lifecycle';
import type { ChartInterval } from '@/lib/chart/types';
import { logger } from '@/lib/telemetry/logger';
import { ChartIntentConviction } from '@/providers/chart-intent-conviction';
import { ChartIntentContext, type ChartIntentStore, createChartIntentStore, type MarketChartIntentSelection, useChartIntentSelection } from '@/stores/chart-intent';
export function ChartIntentProvider(t0) {
  const $ = _c(9);
  const {
    children,
    identityKey
  } = t0;
  let t1;
  if ($[0] !== identityKey) {
    t1 = () => ({
      identityKey,
      store: createChartIntentStore()
    });
    $[0] = identityKey;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [scope, setScope] = useState(t1);
  if (scope.identityKey !== identityKey) {
    setScope({
      identityKey,
      store: createChartIntentStore()
    });
  }
  const {
    store
  } = scope;
  let t2;
  if ($[2] !== identityKey || $[3] !== store) {
    t2 = <ChartIntentHost key={identityKey} store={store} />;
    $[2] = identityKey;
    $[3] = store;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== children || $[6] !== store || $[7] !== t2) {
    t3 = <ChartIntentContext value={store}>{children}{t2}</ChartIntentContext>;
    $[5] = children;
    $[6] = store;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
function ChartIntentHost(t0) {
  const $ = _c(7);
  const {
    store
  } = t0;
  const selection = useChartIntentSelection(store);
  let t1;
  let t2;
  if ($[0] !== selection || $[1] !== store) {
    t1 = () => {
      if (selection === null) {
        return;
      }
      const timeout = setTimeout(() => store.getState().clear(selection.key), 60000);
      return () => clearTimeout(timeout);
    };
    t2 = [selection, store];
    $[0] = selection;
    $[1] = store;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== selection || $[5] !== store) {
    t3 = selection === null ? null : <>{selection.kind === "market" ? <ChartIntentClock /> : null}{selection.kind === "winner" ? <WinnerChartIntent key={selection.key} selection={selection} /> : <ChartIntentMarket key={selection.key} selection={selection} store={store} />}</>;
    $[4] = selection;
    $[5] = store;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function ChartIntentClock() {
  useServerClock();
  return null;
}
function ChartIntentMarket(t0) {
  const $ = _c(10);
  const {
    selection,
    store
  } = t0;
  let t1;
  if ($[0] !== selection.marketPubkey) {
    t1 = {
      pubkey: selection.marketPubkey
    };
    $[0] = selection.marketPubkey;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const market = useGetMarket(t1);
  let t2;
  let t3;
  if ($[2] !== market.isError || $[3] !== selection.marketPubkey) {
    t2 = () => {
      if (!market.isError) {
        return;
      }
      logger.warn("chart.intent_market_load_failed", {
        marketPubkey: selection.marketPubkey
      });
    };
    t3 = [market.isError, selection.marketPubkey];
    $[2] = market.isError;
    $[3] = selection.marketPubkey;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  if (market.data === undefined || market.data.tradingStartedAt === null) {
    return null;
  }
  let t4;
  if ($[6] !== market.data || $[7] !== selection || $[8] !== store) {
    t4 = <ChartIntentQueries selection={selection} store={store} market={market.data} />;
    $[6] = market.data;
    $[7] = selection;
    $[8] = store;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
function ChartIntentQueries(t0) {
  const $ = _c(33);
  const {
    selection,
    store,
    market
  } = t0;
  if (selection.chartMode === "consensus") {
    let t1;
    if ($[0] !== market || $[1] !== selection || $[2] !== store) {
      t1 = <ChartIntentConviction selection={selection} store={store} market={market} />;
      $[0] = market;
      $[1] = selection;
      $[2] = store;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    return t1;
  }
  let chartType;
  let from;
  let interval;
  let t1;
  let to;
  if ($[4] !== market.createdAt || $[5] !== market.resolvedAt || $[6] !== market.status || $[7] !== market.tradingStartedAt || $[8] !== selection.interval || $[9] !== selection.renderMode || $[10] !== selection.startedAt) {
    const lifecycle = resolveTerminalChartLifecycle({
      status: market.status,
      resolvedAt: market.resolvedAt,
      persistedInterval: selection.interval,
      persistedRenderMode: selection.renderMode,
      readOnly: false
    });
    const start = Date.parse(market.tradingStartedAt ?? market.createdAt) / 1000;
    const anchor = lifecycle.resolutionUnix === undefined ? lifecycle.interval === "all" ? selection.startedAt : Math.floor(selection.startedAt / 60000) * 60000 : lifecycle.resolutionUnix * 1000;
    const lifespanSecs = Math.max(0, Math.floor(anchor / 1000) - start);
    interval = resolveAvailableInterval(lifecycle.interval, getAvailableIntervals(lifespanSecs));
    from = resolveChartRequestStart({
      interval,
      marketCreatedAt: start,
      anchorMs: anchor,
      lifespanSecs
    });
    to = lifecycle.resolutionUnix !== undefined ? new Date(lifecycle.resolutionUnix * 1000).toISOString() : undefined;
    chartType = lifecycle.renderMode === "candle" ? "ohlcv" : "line";
    t1 = resolveTerminalChartOpeningEndUnix({
      marketCreatedAtUnix: start,
      resolvedAtUnix: lifecycle.resolutionUnix
    });
    $[4] = market.createdAt;
    $[5] = market.resolvedAt;
    $[6] = market.status;
    $[7] = market.tradingStartedAt;
    $[8] = selection.interval;
    $[9] = selection.renderMode;
    $[10] = selection.startedAt;
    $[11] = chartType;
    $[12] = from;
    $[13] = interval;
    $[14] = t1;
    $[15] = to;
  } else {
    chartType = $[11];
    from = $[12];
    interval = $[13];
    t1 = $[14];
    to = $[15];
  }
  const terminalEnd = t1;
  if (interval === "live" && selection.yAxisProperty === "price") {
    return null;
  }
  let t2;
  if ($[16] !== chartType || $[17] !== from || $[18] !== interval || $[19] !== market.outcomes || $[20] !== selection || $[21] !== store || $[22] !== terminalEnd || $[23] !== to) {
    let t3;
    if ($[25] !== chartType || $[26] !== from || $[27] !== interval || $[28] !== selection || $[29] !== store || $[30] !== terminalEnd || $[31] !== to) {
      t3 = outcome => [<ChartIntentQuery key={`${outcome.outcomeIndex}-main`} selection={selection} store={store} outcomeIndex={outcome.outcomeIndex} interval={interval} from={from} to={to} chartType={chartType} />, ...(terminalEnd === undefined ? [] : [<ChartIntentQuery key={`${outcome.outcomeIndex}-terminal`} selection={selection} store={store} outcomeIndex={outcome.outcomeIndex} interval={interval} from={from} to={new Date(terminalEnd * 1000).toISOString()} chartType="line" terminal={true} />])];
      $[25] = chartType;
      $[26] = from;
      $[27] = interval;
      $[28] = selection;
      $[29] = store;
      $[30] = terminalEnd;
      $[31] = to;
      $[32] = t3;
    } else {
      t3 = $[32];
    }
    t2 = market.outcomes.flatMap(t3);
    $[16] = chartType;
    $[17] = from;
    $[18] = interval;
    $[19] = market.outcomes;
    $[20] = selection;
    $[21] = store;
    $[22] = terminalEnd;
    $[23] = to;
    $[24] = t2;
  } else {
    t2 = $[24];
  }
  return t2;
}
function ChartIntentQuery(t0) {
  const $ = _c(34);
  const {
    selection,
    store,
    outcomeIndex,
    interval,
    from,
    to,
    chartType,
    terminal: t1
  } = t0;
  const terminal = t1 === undefined ? false : t1;
  const t2 = selection.marketPubkey;
  const t3 = String(outcomeIndex);
  const t4 = selection.yAxisProperty === "price" ? "price" : "market_cap";
  const t5 = interval === "live" || terminal ? "1m" : "all";
  const t6 = interval === "live" ? "line" : chartType;
  let t7;
  if ($[0] !== to) {
    t7 = to === undefined ? {} : {
      to
    };
    $[0] = to;
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  let t8;
  if ($[2] !== from || $[3] !== interval || $[4] !== terminal) {
    t8 = terminal ? {
      from
    } : interval === "live" ? {
      liveWindowSeconds: String(LIVE_CHART_SEED_SECONDS)
    } : {
      from,
      targetBars: 180
    };
    $[2] = from;
    $[3] = interval;
    $[4] = terminal;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  let t9;
  if ($[6] !== selection.marketPubkey || $[7] !== t3 || $[8] !== t4 || $[9] !== t5 || $[10] !== t6 || $[11] !== t7 || $[12] !== t8) {
    t9 = {
      pubkey: t2,
      outcomeIndex: t3,
      property: t4,
      interval: t5,
      chartType: t6,
      ...t7,
      ...t8
    };
    $[6] = selection.marketPubkey;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const query = useGetMarketChart(t9);
  let t10;
  let t11;
  if ($[14] !== chartType || $[15] !== interval || $[16] !== outcomeIndex || $[17] !== query.queryKey || $[18] !== selection || $[19] !== store || $[20] !== terminal) {
    t10 = () => {
      store.getState().register(selection, interval, outcomeIndex, query.queryKey, interval === "live" ? "line" : chartType, terminal ? "terminal" : "main");
    };
    t11 = [store, selection, interval, outcomeIndex, query.queryKey, chartType, terminal];
    $[14] = chartType;
    $[15] = interval;
    $[16] = outcomeIndex;
    $[17] = query.queryKey;
    $[18] = selection;
    $[19] = store;
    $[20] = terminal;
    $[21] = t10;
    $[22] = t11;
  } else {
    t10 = $[21];
    t11 = $[22];
  }
  useLayoutEffect(t10, t11);
  let t12;
  let t13;
  if ($[23] !== interval || $[24] !== outcomeIndex || $[25] !== query.data || $[26] !== query.isError || $[27] !== query.isFetchedAfterMount || $[28] !== selection.key || $[29] !== selection.marketPubkey || $[30] !== selection.yAxisProperty || $[31] !== store) {
    t12 = () => {
      if (!query.isError || !query.isFetchedAfterMount || query.data !== undefined) {
        return;
      }
      logger.warn("chart.intent_snapshot_failed", {
        marketPubkey: selection.marketPubkey,
        outcomeIndex,
        interval,
        property: selection.yAxisProperty
      });
      store.getState().clear(selection.key);
    };
    t13 = [query.isError, query.isFetchedAfterMount, query.data, store, selection.key, selection.marketPubkey, selection.yAxisProperty, outcomeIndex, interval];
    $[23] = interval;
    $[24] = outcomeIndex;
    $[25] = query.data;
    $[26] = query.isError;
    $[27] = query.isFetchedAfterMount;
    $[28] = selection.key;
    $[29] = selection.marketPubkey;
    $[30] = selection.yAxisProperty;
    $[31] = store;
    $[32] = t12;
    $[33] = t13;
  } else {
    t12 = $[32];
    t13 = $[33];
  }
  useEffect(t12, t13);
  return null;
}